import type { ScriptStatus, TemplateKey } from './types';

// C8 — the "What we need from you" checklist on the client review page.
// Pure helpers shared by the worker (resolving what the client sees) and the
// admin card (showing the template defaults). The client page only ever
// receives the resolved prompt list, never the modes.

export type ReviewChecklistMode = 'template' | 'custom' | 'off';

/** Per-script setting, stored as JSON in scripts.review_checklist_json.
 *  `prompts` are the custom prompts, kept even while the mode is template or
 *  off so switching back to Custom brings them back. */
export interface ReviewChecklistSetting {
  mode: ReviewChecklistMode;
  prompts: string[];
}

export interface TemplateChecklistDefault {
  enabled: boolean;
  prompts: string[];
}

export const MAX_CHECKLIST_PROMPTS = 4;
export const MAX_CHECKLIST_PROMPT_CHARS = 80;

export const DEFAULT_REVIEW_CHECKLIST_SETTING: ReviewChecklistSetting = {
  mode: 'template',
  prompts: [],
};

const COMPLIANCE = 'Flag anything legal or compliance needs to see';
const SOUNDS_LIKE_YOU = 'Does the opening sound like you?';

/**
 * Per-template defaults (mock-up 07 §01c). These are code defaults until the
 * Settings screen (package 1D) stores per-template overrides; 1D passes its
 * stored values as the optional `defaults` argument below, falling back to
 * this table for any template it has no row for.
 */
export const REVIEW_CHECKLIST_DEFAULTS: Record<TemplateKey, TemplateChecklistDefault> = {
  testimonial: {
    enabled: true,
    prompts: ['Check names, titles and spelling', COMPLIANCE, SOUNDS_LIKE_YOU],
  },
  explainer: {
    enabled: true,
    prompts: [
      'Is every product detail and number correct?',
      'Would a new client follow it first time?',
      COMPLIANCE,
      'Is the call to action the one you want?',
    ],
  },
  brand_film: {
    enabled: true,
    prompts: [
      SOUNDS_LIKE_YOU,
      "Is there anything here you'd never say?",
      'Are the people and places right for the shoot?',
    ],
  },
  social_short: {
    enabled: true,
    prompts: [
      'Does the first line make you stop scrolling?',
      'Are the offer and call to action right?',
      COMPLIANCE,
    ],
  },
  fb_ad_campaign: {
    enabled: true,
    prompts: [
      'Are offers, prices and dates right in every ad?',
      'Does each opening line sound like you?',
      COMPLIANCE,
    ],
  },
  // Briefs start switched off; the prompts are ready if Henry turns them on.
  interview_brief: {
    enabled: false,
    prompts: [
      'Are the right people booked to speak?',
      "Is there any question we shouldn't ask?",
      'Are names and roles spelled correctly?',
    ],
  },
};

export function templateChecklistDefault(
  templateKey: string,
  defaults: Record<TemplateKey, TemplateChecklistDefault> = REVIEW_CHECKLIST_DEFAULTS,
): TemplateChecklistDefault {
  const found = (defaults as Record<string, TemplateChecklistDefault | undefined>)[templateKey];
  if (!found) return { enabled: false, prompts: [] };
  return { enabled: found.enabled, prompts: [...found.prompts] };
}

function cleanPrompt(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

/** Validate prompts typed by Henry: trimmed, empty rows dropped, 4 at most,
 *  80 characters each. */
export function normalizeChecklistPrompts(
  input: unknown,
): { ok: true; prompts: string[] } | { ok: false; error: string } {
  if (!Array.isArray(input) || input.some((p) => typeof p !== 'string')) {
    return { ok: false, error: 'Prompts must be a list of text.' };
  }
  const seen = new Set<string>();
  const prompts = (input as string[]).map(cleanPrompt).filter((p) => {
    const key = p.toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (prompts.length > MAX_CHECKLIST_PROMPTS) {
    return { ok: false, error: `Up to ${MAX_CHECKLIST_PROMPTS} prompts.` };
  }
  if (prompts.some((p) => p.length > MAX_CHECKLIST_PROMPT_CHARS)) {
    return { ok: false, error: `Each prompt can be up to ${MAX_CHECKLIST_PROMPT_CHARS} characters.` };
  }
  return { ok: true, prompts };
}

function isMode(value: unknown): value is ReviewChecklistMode {
  return value === 'template' || value === 'custom' || value === 'off';
}

/** Tolerant parse of the stored column: anything unreadable means template
 *  mode, which is also what a NULL column means. */
export function parseReviewChecklistSetting(json: string | null | undefined): ReviewChecklistSetting {
  if (!json) return { ...DEFAULT_REVIEW_CHECKLIST_SETTING, prompts: [] };
  try {
    const parsed = JSON.parse(json) as { mode?: unknown; prompts?: unknown };
    if (!parsed || typeof parsed !== 'object' || !isMode(parsed.mode)) {
      return { ...DEFAULT_REVIEW_CHECKLIST_SETTING, prompts: [] };
    }
    const prompts = Array.isArray(parsed.prompts)
      ? parsed.prompts
          .filter((p): p is string => typeof p === 'string')
          .map(cleanPrompt)
          .filter((p) => p.length > 0)
          .slice(0, MAX_CHECKLIST_PROMPTS)
          .map((p) => p.slice(0, MAX_CHECKLIST_PROMPT_CHARS))
      : [];
    return { mode: parsed.mode, prompts };
  } catch {
    return { ...DEFAULT_REVIEW_CHECKLIST_SETTING, prompts: [] };
  }
}

const REVIEW_ROUNDS: ReadonlySet<ScriptStatus> = new Set(['v1', 'v2', 'v3']);

/**
 * What the client sees: the per-script override if set, else the template
 * default. Null when the checklist is off, the script is outside v1 to v3
 * (so hidden after approval), or a brief that isn't switched on.
 */
export function resolveReviewChecklist(args: {
  templateKey: string;
  status: ScriptStatus;
  setting: ReviewChecklistSetting;
  defaults?: Record<TemplateKey, TemplateChecklistDefault>;
}): string[] | null {
  if (!REVIEW_ROUNDS.has(args.status)) return null;
  const { setting } = args;
  if (setting.mode === 'off') return null;
  if (setting.mode === 'custom') {
    return setting.prompts.length > 0 ? [...setting.prompts] : null;
  }
  const fallback = templateChecklistDefault(args.templateKey, args.defaults);
  if (!fallback.enabled || fallback.prompts.length === 0) return null;
  return fallback.prompts;
}
